import { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tooltip,
  LinearProgress,
  Snackbar,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import ImageIcon from "@mui/icons-material/Image";
import CampaignIcon from "@mui/icons-material/Campaign";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import UploadAdvertisementModal from "./UploadAdvertisementModal";
import useAdvertisementSocket from "../../hooks/useAdvertisementSocket";
import useAdDownload from "./useAdDownload";
import { tokens } from "../../theme";
import { PageHead, pageContainerSx } from "../../components/ui";
import { AD_DELETE_ERROR_MESSAGE } from "../../utils/errorMessages";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: tokens.warning, bg: tokens.warningSoft },
  approved: { label: "Approved", color: tokens.accent, bg: tokens.accentSoft },
  rejected: { label: "Rejected", color: tokens.danger, bg: tokens.dangerSoft },
  running: { label: "Running", color: tokens.success, bg: tokens.successSoft },
  expired: { label: "Expired", color: tokens.text3, bg: tokens.surface3 },
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 22,
        borderRadius: "6px",
        background: cfg.bg,
        color: cfg.color,
      }}
    />
  );
};

const Kpi = ({ label, value }) => (
  <Card sx={{ p: "13px 14px", display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: tokens.text2 }}>{label}</Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.03em" }}>{value}</Typography>
  </Card>
);

StatusChip.propTypes = {
  status: PropTypes.string,
};

Kpi.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
};

const Advertisements = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [disableId, setDisableId] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const { download, downloadingId, error: downloadError, clearError } = useAdDownload();

  // Keep a stable ref to fetchAds so the socket callback never goes stale
  const fetchAdsRef = useRef(null);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("portalToken");
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/advertisements${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAds(data.ads || []);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchAdsRef.current = fetchAds;
  }, [fetchAds]);
  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // Real-time: when any new advertisement is created, refresh the list
  const handleAdvertisementListUpdated = useCallback(() => {
    if (fetchAdsRef.current) fetchAdsRef.current();
    setSnack({
      open: true,
      msg: "New advertisement added — list updated",
      severity: "info",
    });
  }, []);

  useAdvertisementSocket(handleAdvertisementListUpdated);

  // Surface download failures through the same snackbar as every other action.
  useEffect(() => {
    if (!downloadError) return;
    setSnack({ open: true, msg: downloadError, severity: "error" });
    clearError();
  }, [downloadError, clearError]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/advertisements/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message);
      }
      setAds((prev) => prev.filter((a) => a._id !== deleteId));
      setSnack({
        open: true,
        msg: "Advertisement deleted",
        severity: "success",
      });
    } catch {
      setSnack({ open: true, msg: AD_DELETE_ERROR_MESSAGE, severity: "error" });
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(
        `${CONFIG.BASE_URL}/api/portal/advertisements/${disableId}/disable`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message);
      }
      await fetchAds();
      setSnack({
        open: true,
        msg: "Advertisement disabled",
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        msg: "Failed to disable advertisement",
        severity: "error",
      });
    } finally {
      setDisabling(false);
      setDisableId(null);
    }
  };

  const canEdit = (ad) => ad.approvalStatus === "pending" || ad.approvalStatus === "rejected";
  const canDisable = (ad) => ad.approvalStatus === "approved" || ad.approvalStatus === "running";
  const canDelete = (ad) => ad.approvalStatus !== "expired";
  const countByStatus = (status) => ads.filter((a) => a.approvalStatus === status).length;
  const pendingCount = countByStatus("pending");

  const renderAdsTable = () => {
    if (loading) {
      return (
        <Box>
          <LinearProgress
            sx={{
              borderRadius: 4,
              mb: 2,
              backgroundColor: tokens.surface3,
              "& .MuiLinearProgress-bar": { background: tokens.accent },
            }}
          />
        </Box>
      );
    }

    if (!ads.length) {
      return (
        <Card>
          <CardContent sx={{ py: 6, textAlign: "center" }}>
            <CampaignIcon sx={{ fontSize: 40, color: tokens.text3, mb: 1 }} />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: tokens.text2,
                mb: 0.5,
              }}
            >
              No advertisements yet
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: tokens.text3, mb: 2 }}>
              Upload your first advertisement to get started
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowUpload(true)}
            >
              Upload advertisement
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ overflow: "hidden" }}>
        <TableContainer component={Paper} sx={{ boxShadow: "none", background: "transparent" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: tokens.surface2 }}>
                {["Advertisement", "Type", "Status", "Schedule", "Robots", "Actions"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      color: tokens.text3,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      py: 1.25,
                      borderBottom: `1px solid ${tokens.border}`,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ads.map((ad) => (
                <TableRow
                  key={ad._id}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: tokens.surface2 },
                    "& td": { borderBottom: `1px solid ${tokens.border}` },
                  }}
                >
                  <TableCell sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          background: tokens.surface3,
                        }}
                      >
                        {ad.mediaType === "video" ? (
                          <VideoFileIcon sx={{ fontSize: 15, color: tokens.text2 }} />
                        ) : (
                          <ImageIcon sx={{ fontSize: 15, color: tokens.text2 }} />
                        )}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: tokens.text,
                            lineHeight: 1.2,
                          }}
                        >
                          {ad.advertisementName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                          {ad.category || "Uncategorized"}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ad.mediaType === "video" ? "Video" : "Image"}
                      size="small"
                      sx={{
                        fontSize: 11,
                        height: 20,
                        fontWeight: 600,
                        background: tokens.surface3,
                        color: tokens.text2,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={ad.approvalStatus} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: tokens.text2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ad.startDate
                        ? new Date(ad.startDate).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                          })
                        : "—"}{" "}
                      {ad.endDate
                        ? `→ ${new Date(ad.endDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        ad.assignedRobots?.length
                          ? ad.assignedRobots.map((r) => r.name).join(", ")
                          : "No robots assigned"
                      }
                      arrow
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: tokens.text2,
                          display: "inline-block",
                        }}
                      >
                        {ad.assignedRobots?.length || 0} robots
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={0.5}>
                      <Tooltip title="View Details" arrow>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/advertisements/${ad._id}`)}
                          sx={{
                            color: tokens.text3,
                            "&:hover": {
                              color: tokens.accent,
                              background: tokens.accentSoft,
                            },
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={ad.mediaType === "video" ? "Download video" : "Download image"}
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => download(ad)}
                            disabled={downloadingId === ad._id}
                            sx={{
                              color: tokens.text3,
                              "&:hover": {
                                color: tokens.accent,
                                background: tokens.accentSoft,
                              },
                            }}
                          >
                            {downloadingId === ad._id ? (
                              <CircularProgress size={14} sx={{ color: tokens.accent }} />
                            ) : (
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canEdit(ad) && (
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingAd(ad);
                              setShowUpload(true);
                            }}
                            sx={{
                              color: tokens.text3,
                              "&:hover": {
                                color: tokens.accent,
                                background: tokens.accentSoft,
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDisable(ad) && (
                        <Tooltip title="Disable" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDisableId(ad._id)}
                            sx={{
                              color: tokens.text3,
                              "&:hover": {
                                color: tokens.danger,
                                background: tokens.dangerSoft,
                              },
                            }}
                          >
                            <BlockIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete(ad) && (
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteId(ad._id)}
                            sx={{
                              color: tokens.text3,
                              "&:hover": {
                                color: tokens.danger,
                                background: tokens.dangerSoft,
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  return (
    <Box sx={pageContainerSx()}>
      {/* Header */}
      <PageHead
        title="Advertisements"
        sub="Manage the campaigns shown on your robots"
        icon="megaphone"
        actions={
          <>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: 12.5,
                  height: 36,
                  borderRadius: "9px",
                  background: tokens.surface2,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: tokens.border,
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 12.5 }}>
                  All Status
                </MenuItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k} sx={{ fontSize: 12.5 }}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingAd(null);
                setShowUpload(true);
              }}
            >
              Upload AD
            </Button>
          </>
        }
      />

      {/* KPI row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1.5,
          mb: 1.75,
        }}
      >
        <Kpi label="Total ads" value={ads.length} />
        <Kpi label="Running" value={countByStatus("running")} />
        <Kpi label="Pending" value={pendingCount} />
        <Kpi label="Approved" value={countByStatus("approved")} />
        <Kpi label="Rejected" value={countByStatus("rejected")} />
      </Box>

      {pendingCount > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: "10px 14px",
            mb: 1.5,
            borderRadius: "10px",
            background: tokens.warningSoft,
            border: `1px solid ${tokens.warning}30`,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: tokens.text, fontWeight: 500 }}>
            {pendingCount} campaign{pendingCount === 1 ? "" : "s"} awaiting Botriq review — usually
            approved within 24 hours.
          </Typography>
        </Box>
      )}

      {/* Table */}
      {renderAdsTable()}

      {/* Upload/Edit Modal */}
      {showUpload && (
        <UploadAdvertisementModal
          open={showUpload}
          onClose={() => {
            setShowUpload(false);
            setEditingAd(null);
          }}
          onSuccess={() => {
            setShowUpload(false);
            setEditingAd(null);
            fetchAds();
            setSnack({
              open: true,
              msg: editingAd ? "Advertisement updated" : "Advertisement uploaded!",
              severity: "success",
            });
          }}
          editingAd={editingAd}
        />
      )}

      {/* Disable Confirm */}
      <Dialog
        open={!!disableId}
        onClose={() => setDisableId(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "16px" } } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Disable Advertisement</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
            Are you sure you want to disable this advertisement? It will stop playing on your robots
            immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDisableId(null)}
            variant="outlined"
            size="small"
            sx={{ borderColor: tokens.border, color: tokens.text }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisable}
            variant="contained"
            color="error"
            size="small"
            disabled={disabling}
          >
            {disabling ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "16px" } } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete Advertisement</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
            Are you sure you want to delete this advertisement? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteId(null)}
            variant="outlined"
            size="small"
            sx={{ borderColor: tokens.border, color: tokens.text }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            size="small"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} sx={{ borderRadius: "10px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Advertisements;
